/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable react/no-unstable-nested-components */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { DatePicker, Popover, Spin } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { json2csv } from "json-2-csv";
import CustomTable from "../../../Core/Components/CustomTable";
import CustomSearchAndSelect from "../../../Core/Components/CustomSearchAndSelect";
import Button from "../../../Core/Components/CustomButton";
import CloseIcon from "../../../Common/Svgs/CloseIcon";
import { ExportTertiaryIcon } from "../../../Common/Svgs/ExportIcon";
import TrikeIcon from "../../../Common/Svgs/TrikeIcon";
import { getTripsForTrikeV3 } from "../../../Redux/APIs/tripsAPI";
import { getRentalsForTrikeV3 } from "../../../Redux/APIs/rentalsAPI";
import { client } from "../../../Utils/axiosClient";
import { trikesDetailsColumns } from "../Data/TrikesDetailData";
import useDriverOptions from "../phase3/useDriverOptions";
import { rentalTrikeColumns } from "./rentalTrikeColumns";
import { DEFAULT_LIMIT } from "../../../Utils/constants";

const getTripStatusText = (statusId) => {
  if (statusId === 1) return "Ongoing";
  if (statusId === 2) return "Upcoming";
  if (statusId === 3) return "Completed";
  return "--";
};

const TrikeTripsAndRentals = () => {
  const dispatch = useDispatch();

  const { currentTrike } = useSelector((state) => state.trikes);
  const { tripsForTrike, loading: tripsLoading } = useSelector(
    (state) => state.trips,
  );
  const { rentalsForTrike, rentalsForTrikeLoading } = useSelector(
    (state) => state.rentals,
  );

  const drivers = useDriverOptions();

  const [activeTab, setActiveTab] = useState("trips");
  const [tripsPage, setTripsPage] = useState(1);
  const [rentalsPage, setRentalsPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_LIMIT);

  // Filter Popover state matching MoreTrikeFilters in Assets
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [finalDriverList, setFinalDriverList] = useState([]);
  const [filterDateRangeStart, setFilterDateRangeStart] = useState("");
  const [filterDateRangeEnd, setFilterDateRangeEnd] = useState("");
  const [filterDrivers, setFilterDrivers] = useState([]);

  const [appliedFilters, setAppliedFilters] = useState({
    dateRangeStart: "",
    dateRangeEnd: "",
    drivers: [],
  });

  const [exportLoading, setExportLoading] = useState(false);

  const tableRef = useRef(null);

  const driversList = useMemo(() => {
    return (drivers || [])
      .map((item) => ({ id: item.id, name: item.value }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [drivers]);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (appliedFilters.dateRangeStart && appliedFilters.dateRangeEnd)
      count += 1;
    if (appliedFilters.drivers?.length > 0) count += 1;
    return count;
  }, [appliedFilters]);

  const handleDateRangeChange = (e) => {
    if (e && e.length === 2 && e[0] && e[1]) {
      setFilterDateRangeStart(e[0].format("YYYY-MM-DD"));
      setFilterDateRangeEnd(e[1].format("YYYY-MM-DD"));
    }
  };

  // Fetch initial counts and current tab data
  useEffect(() => {
    if (!currentTrike?.id) return;

    const driverIds = (appliedFilters.drivers || []).map((d) => d.id).join(",");
    const driverNames = (appliedFilters.drivers || [])
      .map((d) => d.name)
      .join(",");

    const tripsParams = {
      offset: (tripsPage - 1) * pageSize,
      limit: pageSize,
      trikeId: currentTrike.id,
      startDate: appliedFilters.dateRangeStart || undefined,
      endDate: appliedFilters.dateRangeEnd || undefined,
      driverIds: driverIds || undefined,
      driverName: driverNames || undefined,
    };

    const rentalsParams = {
      offset: (rentalsPage - 1) * pageSize,
      limit: pageSize,
      trikeId: currentTrike.id,
      startDate: appliedFilters.dateRangeStart || undefined,
      endDate: appliedFilters.dateRangeEnd || undefined,
      driverIds: driverIds || undefined,
      driverName: driverNames || undefined,
    };

    if (activeTab === "trips") {
      dispatch(getTripsForTrikeV3(tripsParams));
    } else {
      dispatch(getRentalsForTrikeV3(rentalsParams));
    }
  }, [
    dispatch,
    currentTrike?.id,
    activeTab,
    tripsPage,
    rentalsPage,
    pageSize,
    appliedFilters,
  ]);

  // Initial fetch for the other tab's count on mount or trike change
  useEffect(() => {
    if (!currentTrike?.id) return;

    if (activeTab === "trips") {
      dispatch(
        getRentalsForTrikeV3({
          offset: 0,
          limit: 1,
          trikeId: currentTrike.id,
        }),
      );
    } else {
      dispatch(
        getTripsForTrikeV3({
          offset: 0,
          limit: 1,
          trikeId: currentTrike.id,
        }),
      );
    }
  }, [dispatch, currentTrike?.id]);

  const handleApplyFilters = () => {
    setAppliedFilters({
      dateRangeStart: filterDateRangeStart,
      dateRangeEnd: filterDateRangeEnd,
      drivers: filterDrivers,
    });
    setTripsPage(1);
    setRentalsPage(1);
    setPopoverOpen(false);
  };

  const handleClearFilters = () => {
    setFilterDateRangeStart("");
    setFilterDateRangeEnd("");
    setFilterDrivers([]);
    setFinalDriverList([]);
    setAppliedFilters({
      dateRangeStart: "",
      dateRangeEnd: "",
      drivers: [],
    });
    setTripsPage(1);
    setRentalsPage(1);
    setPopoverOpen(false);
  };

  // Export CSV handler
  const handleExportCSV = async () => {
    if (!currentTrike?.id) return;
    try {
      setExportLoading(true);

      const driverIds = (appliedFilters.drivers || [])
        .map((d) => d.id)
        .join(",");
      const driverNames = (appliedFilters.drivers || [])
        .map((d) => d.name)
        .join(",");

      if (activeTab === "trips") {
        const response = await client.get(
          `${process.env.REACT_APP_API_URL}/trip/trips-for-trike`,
          {
            params: {
              trikeId: currentTrike.id,
              startDate: appliedFilters.dateRangeStart || undefined,
              endDate: appliedFilters.dateRangeEnd || undefined,
              driverIds: driverIds || undefined,
              driverName: driverNames || undefined,
              exportAll: true,
            },
          },
        );

        const records = response?.data?.response?.result || [];
        if (!records.length) {
          setExportLoading(false);
          return;
        }

        const formattedTrips = records.map((item) => ({
          "Trip ID": item.id,
          Driver: item.driverName || "--",
          "No. Of Stops": item.stops?.length || 2,
          Date: item.startDate
            ? dayjs(item.startDate).format("ddd, MMM DD YYYY")
            : "--",
          "Start Time": item.startTime
            ? dayjs(item.startTime).format("hh:mm A")
            : "--",
          Status: getTripStatusText(item.statusId),
        }));

        const csv = json2csv(formattedTrips, { emptyFieldValue: "--" });
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const link = document.createElement("a");
        const url = URL.createObjectURL(blob);
        link.setAttribute("href", url);
        link.setAttribute(
          "download",
          `Trips_Trike_${currentTrike.id}_${dayjs().format("YYYY-MM-DD")}.csv`,
        );
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        const response = await client.get(
          `${process.env.REACT_APP_API_URL}/rentals/rentals-for-trike`,
          {
            params: {
              trikeId: currentTrike.id,
              startDate: appliedFilters.dateRangeStart || undefined,
              endDate: appliedFilters.dateRangeEnd || undefined,
              driverIds: driverIds || undefined,
              driverName: driverNames || undefined,
              exportAll: true,
            },
          },
        );

        const records = response?.data?.response?.result || [];
        if (!records.length) {
          setExportLoading(false);
          return;
        }

        const formattedRentals = records.map((item) => {
          const driverName = item.firstName
            ? `${item.firstName} ${item.lastName}`
            : item.driverName || "--";
          const dateVal = item.startTime || item.submittedAt || item.pickupDate;
          let formattedTime = "--";
          if (item.startTime) {
            formattedTime = dayjs(item.startTime).format("hh:mm A");
          } else if (item.pickupTime) {
            formattedTime = dayjs(item.pickupTime, "HH:mm:ss").format(
              "hh:mm A",
            );
          } else if (item.submittedAt) {
            formattedTime = dayjs(item.submittedAt).format("hh:mm A");
          }

          let statusText = "Ongoing";
          const numStatus = Number(item.status);
          if (numStatus === 2) statusText = "Completed";
          else if (numStatus === 5) statusText = "Upcoming";

          return {
            "Rental ID": item.id,
            Driver: driverName,
            Date: dateVal ? dayjs(dateVal).format("ddd, MMM DD YYYY") : "--",
            "Start Time": formattedTime,
            Status: statusText,
          };
        });

        const csv = json2csv(formattedRentals, { emptyFieldValue: "--" });
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const link = document.createElement("a");
        const url = URL.createObjectURL(blob);
        link.setAttribute("href", url);
        link.setAttribute(
          "download",
          `Rentals_Trike_${currentTrike.id}_${dayjs().format(
            "YYYY-MM-DD",
          )}.csv`,
        );
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Export CSV Error:", error);
    } finally {
      setExportLoading(false);
    }
  };

  const tripsCount = Number(tripsForTrike?.totalRecords || 0);
  const rentalsCount = Number(rentalsForTrike?.totalRecords || 0);

  const currentColumns =
    activeTab === "trips" ? trikesDetailsColumns : rentalTrikeColumns;
  const currentData =
    activeTab === "trips"
      ? tripsForTrike?.result || []
      : rentalsForTrike?.result || [];
  const currentTotal = activeTab === "trips" ? tripsCount : rentalsCount;
  const currentPage = activeTab === "trips" ? tripsPage : rentalsPage;
  const currentLoading =
    activeTab === "trips" ? tripsLoading : rentalsForTrikeLoading;

  return (
    <div className="bg-background-white border-y border-light-grey pb-2">
      {/* Header bar with tabs and actions */}
      <div className="flex items-center justify-between px-6 py-2">
        {/* Left: Pill Tabs */}
        <div className="flex items-center gap-x-3">
          <button
            type="button"
            onClick={() => setActiveTab("trips")}
            className={`flex items-center gap-x-2 px-6 py-2 shadow-sm rounded-md font-poppins text-xs font-medium transition-all ${
              activeTab === "trips"
                ? "bg-green text-white "
                : "bg-white   text-dark-gray hover:bg-background"
            }`}
          >
            <span>Trips</span>
            <span
              className={`flex items-center justify-center rounded-full px-1 py-1 h-5 w-5 font-aileron text-[10px] font-semibold ${
                activeTab === "trips"
                  ? "bg-white text-green"
                  : "bg-light-grey text-dark-gray"
              }`}
            >
              {tripsCount}
            </span>
          </button>

          <button
            type="button"
            onClick={() => setActiveTab("rentals")}
            className={`flex items-center gap-x-2 px-6 py-2 shadow-sm rounded-md font-poppins text-xs font-medium transition-all ${
              activeTab === "rentals"
                ? "bg-green text-white "
                : "bg-white   text-dark-gray hover:bg-background"
            }`}
          >
            <span>Rentals</span>
            <span
              className={`flex items-center justify-center rounded-full px-1 py-1 h-5 w-5 font-aileron text-[10px] font-semibold ${
                activeTab === "rentals"
                  ? "bg-white text-green"
                  : "bg-light-grey text-dark-gray"
              }`}
            >
              {rentalsCount}
            </span>
          </button>
        </div>

        {/* Right: Actions (Filters & Export) */}
        <div className="flex items-center gap-x-3">
          {/* Filters Button with Popover matching MoreTrikeFilters in Assets */}
          <Popover
            open={popoverOpen}
            onOpenChange={setPopoverOpen}
            trigger="click"
            placement="bottomRight"
            arrow={false}
            content={
              <div className="bg-background-white w-78 rounded-md p-3 shadow-moreFilters">
                <div className="flex items-center justify-between">
                  <div className="text-base font-medium text-black font-poppins">
                    Filters
                  </div>
                  <CloseIcon
                    className="w-5 h-5 cursor-pointer stroke-dark-gray"
                    onClick={() => setPopoverOpen(false)}
                  />
                </div>

                <div className="mt-6">
                  <div className="font-normal font-poppins text-xs leading-[14.4px] text-dark-gray mb-2">
                    Date Range
                  </div>
                  <DatePicker.RangePicker
                    value={[
                      filterDateRangeStart !== "" &&
                        dayjs(filterDateRangeStart),
                      filterDateRangeEnd !== "" && dayjs(filterDateRangeEnd),
                    ]}
                    format="DD MMM YYYY"
                    separator={<div className="text-dark-gray"> - </div>}
                    onCalendarChange={(e) => handleDateRangeChange(e)}
                    allowClear={false}
                    className="h-[39px] w-full"
                    disabledDate={(current) => current.isAfter(dayjs())}
                  />

                  <div className="font-normal font-poppins text-xs leading-[14.4px] text-dark-gray mt-6">
                    Driver
                  </div>
                  <CustomSearchAndSelect
                    placeholder="Select Driver"
                    inputList={driversList}
                    finalList={finalDriverList}
                    setFinalList={setFinalDriverList}
                    onChange={(selected) => setFilterDrivers(selected)}
                  />
                </div>

                <div className="flex mt-6 gap-x-3">
                  <Button
                    size="primary"
                    width="save"
                    colors="grey"
                    onClick={handleClearFilters}
                  >
                    Clear All
                  </Button>
                  <Button
                    size="primary"
                    width="save"
                    type="submit"
                    onClick={handleApplyFilters}
                  >
                    Apply Filters
                  </Button>
                </div>
              </div>
            }
          >
            {activeFilterCount > 0 ? (
              <div className="flex items-center gap-x-3">
                <button
                  type="button"
                  className="px-3 py-[11px] bg-green rounded-md flex items-center gap-x-3 h-10"
                >
                  <p className="font-poppins text-xs text-background-white">
                    Filters
                  </p>
                  <div className="w-4.5 h-4.5 rounded-full flex items-center justify-center bg-background-white">
                    <p className="text-10 font-aileron text-black font-semibold">
                      {activeFilterCount}
                    </p>
                  </div>
                </button>
                <button
                  type="button"
                  className="px-3 py-[11px] rounded-md hover:bg-background h-10"
                  onClick={handleClearFilters}
                >
                  <p className="font-poppins text-xs text-black font-medium">
                    Clear All
                  </p>
                </button>
              </div>
            ) : (
              <button
                type="button"
                className="flex items-center gap-x-3 px-3 py-[11px] hover:bg-background rounded-md h-10"
              >
                <img src="/assets/icons/sort.svg" alt="Filters" />
                <p className="font-poppins text-xs leading-5 font-medium text-dark-gray">
                  Filters
                </p>
              </button>
            )}
          </Popover>

          {/* Export Button */}
          <button
            type="button"
            disabled={exportLoading}
            onClick={handleExportCSV}
            className="flex items-center gap-x-2 px-3 py-[11px] rounded-md hover:bg-background text-dark-gray transition-all disabled:opacity-50 h-10"
          >
            {exportLoading ? (
              <Spin
                indicator={<LoadingOutlined className="text-dark-gray" spin />}
              />
            ) : (
              <ExportTertiaryIcon size={16} className="text-dark-gray" />
            )}
            <span className="font-poppins text-xs font-medium">Export</span>
          </button>
        </div>
      </div>

      <div className="w-full h-[1px] bg-light-grey" />

      {/* Table Section */}
      <div
        className={`h-[calc(100vh-544px)] ${
          currentData.length && "overflow-y-scroll"
        }`}
        ref={tableRef}
      >
        <CustomTable
          disableScroll
          columns={currentColumns}
          data={currentData}
          loading={currentLoading}
          emptyState={<TrikeIcon size={16} className="text-dark-gray" />}
          customTableClassName="w-full"
          pagination={{
            pageSize,
            total: currentTotal,
            showSizeChanger: true,
            onShowSizeChange: (current, size) => {
              setPageSize(size);
              if (activeTab === "trips") {
                setTripsPage(1);
              } else {
                setRentalsPage(1);
              }
            },
            onChange: (page, size) => {
              if (size && size !== pageSize) {
                setPageSize(size);
                if (activeTab === "trips") {
                  setTripsPage(1);
                } else {
                  setRentalsPage(1);
                }
              } else if (activeTab === "trips") {
                setTripsPage(page);
              } else {
                setRentalsPage(page);
              }
            },
            current: currentPage,
          }}
        />
      </div>
    </div>
  );
};

export default TrikeTripsAndRentals;
